import { detectHomeRegion } from "./home-region-detect";

/**
 * Desktop-side home-region resolver.
 *
 * Returns the user's home region (`us` | `eu`). Cache-first, then a
 * synchronous client-side timezone-based detection on miss — no network.
 *
 * Option F.3 (2026-05-20): the CF Worker `/route` call has been removed.
 * Before: cold start → `fetchRouteResolver` (cf-ipcountry + optional
 *   timezone) → cache → return.
 * After:  cold start → `detectHomeRegion()` (IANA timezone, purely
 *   client-side) → cache → return.
 *
 * Region routing is static per sign-in. A previous reactive
 * reconciler (`HomeRegionWatcher`, v0.1.58) read the authoritative
 * `home_region` from the Zero `users` row and updated the sync URL
 * mid-session whenever the server's value differed from the client
 * guess. It was removed because flipping `cacheURL` mid-session
 * violates a Zero invariant — cookie + clientGroupID are bound to
 * the connected zero-cache backend, but the JS Zero client uses the
 * same IDB store regardless of `cacheURL`, so the new replica sees
 * stale identity from the old one and rejects the connection,
 * cascading into an infinite ZeroProvider remount loop. The
 * localStorage cache is cleared on sign-out (see `logout.ts`) so a
 * traveler picks up the new region on the next sign-in.
 *
 * The per-workspace dynamic resolution that lived here pre-2026-05-19
 * (`prefetchRegionalUrls`, `getRegionalUrls`, `pickActiveSyncUrl`,
 * `evictCacheIfRegionDiffers`, the `ALLOWED_SYNC_HOSTS` allowlist, the
 * `RegionalUrls` type) was deleted as part of Option D — see
 * `.claude/docs/multi-region-runbook.md` § "Desktop client region
 * machinery" for the rationale + a reintroduction checklist if EU PG
 * authority is ever brought back.
 */

const HOME_REGION_CACHE_KEY = "ano:home-region:v1";
const BUILD_SHA_KEY = "ano:home-region:v1:__build_sha";

/**
 * Resolve the caller's home region. Cache-first, timezone-fallback.
 *
 * Returns `'us'` or `'eu'`. This function is the SOLE owner of
 * localStorage persistence for `home_region` — it writes after the
 * client-side detection so the next cold start hits the fast path
 * without re-running the (admittedly cheap) timezone read.
 *
 * Synchronous in practice: no network, no async work. Kept as
 * `async` so existing callers (the AuthGate sign-in effect) don't need
 * a signature churn. The eventual `await` resolves on the microtask
 * queue.
 */
export async function resolveHomeRegion(): Promise<"us" | "eu"> {
  // Cached value wins. Set by a previous sign-in; survives restart.
  if (typeof window !== "undefined") {
    try {
      const cached = window.localStorage.getItem(HOME_REGION_CACHE_KEY);
      if (cached === "us" || cached === "eu") return cached;
    } catch {
      // Private browsing / quota — fall through to detection.
    }
  }

  const region = detectHomeRegion();
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(HOME_REGION_CACHE_KEY, region);
    } catch {
      // Cache write failed; we'll just re-detect next launch.
    }
  }
  return region;
}

/**
 * One-shot eviction of the home-region cache when the build SHA
 * changes. Called from `main.tsx` at app startup so an app upgrade
 * can self-heal stale cache without forcing the user to wait for
 * a manual invalidation.
 *
 * Build SHA is read from a `BUILD_SHA_KEY` sentinel. Cleared once per
 * app version, then re-stamped.
 */
export function evictCacheOnBuildShaChange(currentSha: string): boolean {
  if (!currentSha) return false;
  if (typeof window === "undefined") return false;
  try {
    const previous = window.localStorage.getItem(BUILD_SHA_KEY);
    if (previous === currentSha) return false;
    window.localStorage.removeItem(HOME_REGION_CACHE_KEY);
    window.localStorage.setItem(BUILD_SHA_KEY, currentSha);
    return true;
  } catch {
    return false;
  }
}

/**
 * Test helper — wipes the home-region cache entries. Production code
 * should never call this; consumers don't need to invalidate because
 * home_region is sticky per install.
 */
export function __clearRegionResolverCache(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(HOME_REGION_CACHE_KEY);
    window.localStorage.removeItem(BUILD_SHA_KEY);
  } catch {
    // Ignore.
  }
}
